import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { ArrowRight, Activity, Cigarette, Wine, Apple, Moon, Stethoscope, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import { MiniQuestionnaireData, initialMiniQuestionnaireData } from "@/types/miniQuestionnaire";
import { getEligibleMiniDepistages } from "@/utils/miniDepistageUtils";
import {
  getRenforcementAdvice,
  getTabacAdvice,
  getAlcoolAdvice,
  getViandeRougeAdvice,
  getRegimeMediterraneenAdvice,
  getSommeilAdvice
} from "@/utils/miniQuestionnaireAdvice";

const MiniResultats = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<MiniQuestionnaireData>(initialMiniQuestionnaireData);

  useEffect(() => {
    const savedData = localStorage.getItem("miniQuestionnaireData");
    if (savedData) {
      setData(JSON.parse(savedData));
    } else {
      navigate("/mini-questionnaire");
    }
  }, [navigate]);

  const eligibleDepistages = getEligibleMiniDepistages(data);
  const depistagesAJour = eligibleDepistages.filter(d => 
    data.depistages_effectues.includes(d.id)
  );
  const depistagesAFaire = eligibleDepistages.filter(d => 
    !data.depistages_effectues.includes(d.id)
  );

  const handleDoctolibClick = () => {
    window.open("https://www.doctolib.fr", "_blank");
  };

  const sections = [
    {
      id: "activite",
      title: "Activité physique",
      icon: Activity,
      advice: getRenforcementAdvice(data),
      color: "text-green-600"
    },
    {
      id: "tabac",
      title: "Tabac",
      icon: Cigarette,
      advice: getTabacAdvice(data),
      color: "text-orange-600"
    },
    {
      id: "alcool",
      title: "Alcool",
      icon: Wine,
      advice: getAlcoolAdvice(data),
      color: "text-purple-600"
    },
    {
      id: "nutrition-viande",
      title: "Nutrition – Viande rouge",
      icon: Apple,
      advice: getViandeRougeAdvice(data),
      color: "text-red-600"
    },
    {
      id: "nutrition-mediterraneen",
      title: "Nutrition – Régime méditerranéen",
      icon: Apple,
      advice: getRegimeMediterraneenAdvice(data),
      color: "text-emerald-600"
    },
    {
      id: "sommeil",
      title: "Sommeil",
      icon: Moon,
      advice: getSommeilAdvice(data),
      color: "text-blue-600"
    }
  ].filter(section => section.advice);

  return (
    <div className="min-h-screen bg-feel-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl md:text-4xl font-bold text-feel-foreground mb-4">
            Votre Bilan Santé Personnalisé
          </h1>
          <p className="text-feel-secondary-foreground text-lg">
            Voici vos résultats basés sur vos réponses au questionnaire
          </p>
        </div>

        {/* Conseils par section */}
        <Card className="mb-8 p-6">
          <h2 className="text-xl font-semibold text-feel-foreground mb-4 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-feel-primary" />
            Vos conseils personnalisés
          </h2>
          
          <Accordion type="multiple" className="w-full">
            {sections.map((section) => (
              <AccordionItem key={section.id} value={section.id}>
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-3">
                    <section.icon className={`w-5 h-5 ${section.color}`} />
                    <span className="font-medium">{section.title}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="prose prose-sm max-w-none text-feel-secondary-foreground whitespace-pre-line pl-8">
                    {section.advice}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>

        {/* Tableau des dépistages */}
        {eligibleDepistages.length > 0 && (
          <Card className="mb-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-feel-foreground flex items-center gap-2">
                <Stethoscope className="w-5 h-5 text-feel-primary" />
                Tableau de vos dépistages
              </h2>
              {depistagesAFaire.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleDoctolibClick}
                  className="flex items-center gap-2"
                >
                  Prendre RDV sur Doctolib
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-red-600">Dépistages à faire</TableHead>
                  <TableHead className="text-green-600">Dépistages à jour</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from({ 
                  length: Math.max(depistagesAFaire.length, depistagesAJour.length, 1) 
                }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell className="text-feel-secondary-foreground">
                      {depistagesAFaire[index]?.title || ""}
                    </TableCell>
                    <TableCell className="text-feel-secondary-foreground">
                      {depistagesAJour[index]?.title || ""}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}

        {/* CTA Premium - Poursuivre l'évaluation */}
        <div className="mt-16 mb-8">
          <div className="text-center max-w-xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-6">
              Ce bilan n'est pas terminé.
            </h2>
            <p className="text-muted-foreground text-base leading-relaxed mb-8">
              Cette première évaluation pose les bases.
              <br />
              FEEL peut approfondir ce qui a déjà été analysé
              <br />
              et explorer d'autres dimensions essentielles de votre santé.
            </p>
            <Button 
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3"
              onClick={() => navigate("/acces-bilan-complet")}
            >
              Poursuivre l'évaluation complète
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default MiniResultats;
